<template>
  <div>
    <Breadcrumb :breadcrumb="breadcrumb"></Breadcrumb>
    <h5 class="title is-5">{{ $t("devices.edit") }}</h5>

    <div class="content-box">
      <Form
        @submit="onSubmit"
        :validation-schema="schema"
        :initial-values="formValues"
        v-slot="{ errors }"
      >
        <div class="field">
          <label class="label">{{ $t("devices.deviceId") }} *</label>
          <div class="control">
            <Field
              name="deviceId"
              class="input"
              type="text"
              placeholder="ex) 0000000000"
              maxlength="12"
              required="required"
              :class="{ 'is-danger': errors.deviceId }"
              disabled
            />
            <span class="help is-danger">{{ errors.deviceId }}</span>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("devices.deviceType") }} *</label>
          <div class="control">
            <div class="select">
              <Field
                name="deviceType"
                as="select"
                v-model="deviceType"
                :class="{ 'is-danger': errors.deviceType }"
              >
                <option value="TrailCamera">TrailCamera</option>
              </Field>
            </div>
            <span class="help is-danger">{{ errors.deviceType }}</span>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("devices.deviceLocate") }} *</label>
          <div class="control">
            <Field
              name="deviceLocate"
              class="input"
              type="text"
              placeholder="ex) 製造ラインA"
              maxlength="60"
              required="required"
              :class="{ 'is-danger': errors.deviceLocate }"
            />
            <span class="help is-danger">{{ errors.deviceLocate }}</span>
          </div>
        </div>

        <div class="field" v-if="deviceType === 'TrailCamera'">
          <label class="label">
            <div>
              {{ $t("devices.aiLevel") }}
              <span
                class="icon"
                style="cursor: pointer;"
                @click="openAiLevelHelpModal"
              >
                <i class="fas fa-question-circle"></i>
              </span>
            </div>
          </label>
          <div class="select">
            <Field name="aiLevel" as="select">
              <option value="High">{{ $t("devices.highAILevel") }}</option>
              <option value="Mid">{{ $t("devices.midAILevel") }}</option>
              <option value="Low">{{ $t("devices.lowAILevel") }}</option>
            </Field>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("devices.address") }}</label>
          <div class="control">
            <Field
              name="address"
              class="input"
              type="text"
              placeholder="ex) 東京都豊島区東池袋3-15-15"
              :class="{ 'is-danger': errors.address }"
            />
            <span class="help is-danger">{{ errors.address }}</span>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("Note") }}</label>
          <div class="control">
            <Field
              name="note"
              class="input"
              type="text"
              :class="{ 'is-danger': errors.note }"
            />
            <span class="help is-danger">{{ errors.note }}</span>
          </div>
        </div>

        <router-link
          :to="{
            name: 'devicesView',
            params: { deviceId: $route.params.deviceId },
          }"
          class="button"
          :data-tooltip="$t('Back')"
        >
          Cancel
        </router-link>

        <button
          type="submit"
          class="button is-primary ml-3"
          v-bind:class="{ 'is-loading': isLoad }"
        >
          {{ $t("Confirm") }}
        </button>
      </Form>
    </div>
  </div>
  <AiLevelHelpJa
    v-if="lang === 'ja'"
    :isHelpModalOpen="aiLevelHelpModal"
    @close="aiLevelHelpModal = false"
  />
  <AiLevelHelpEnus
    v-if="lang === 'en-US'"
    :isHelpModalOpen="aiLevelHelpModal"
    @close="aiLevelHelpModal = false"
  />
</template>

<script>
import Breadcrumb from "@/components/Breadcrumb";
import AiLevelHelpEnus from "@/components/devices/AiLevelHelpEnus.vue";
import AiLevelHelpJa from "@/components/devices/AiLevelHelpJa.vue";
import imsr from "@/imsr-api";
import { computed, ref } from "@vue/reactivity";
import { Field, Form } from "vee-validate";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import * as Yup from "yup";

export default {
  components: {
    Form,
    Field,
    Breadcrumb,
    AiLevelHelpJa,
    AiLevelHelpEnus,
  },
  setup() {
    const { t } = useI18n();
    const { locale } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const isLoad = ref(false);
    const formValues = ref({});
    const deviceType = ref("");
    const aiLevelHelpModal = ref(false);
    const breadcrumb = ref([
      {
        title: "Home",
        path: {
          name: "dashboard",
          params: { projectId: route.params.projectId },
        },
      },
      {
        title: "Device Config",
        path: {
          name: "devicesList",
          params: { projectId: route.params.projectId },
        },
      },
      {
        title: route.params.deviceId,
        path: {
          name: "devicesView",
          params: {
            projectId: route.params.projectId,
            deviceId: route.params.deviceId,
          },
        },
      },
      { title: "Edit Device", path: "#", isActive: true },
    ]);

    const getDevice = async () => {
      try {
        const params = {
          tenantId: store.getters.tenantId,
          projectId: route.params.projectId,
          deviceId: route.params.deviceId,
        };
        formValues.value = await imsr.imsrGetApi("/devices", params);
        if (!formValues.value.aiLevel || formValues.value.aiLevel === "") {
          formValues.value.aiLevel = "Mid";
        }
        deviceType.value = formValues.value.deviceType;
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };
    getDevice();

    const schema = Yup.object().shape({
      deviceId: Yup.string().required(t("validate.required")),
      deviceLocate: Yup.string().required(t("validate.required")),
    });

    const onSubmit = async (values) => {
      try {
        isLoad.value = true;
        values.tenantId = store.getters.tenantId;
        values.projectId = route.params.projectId;
        await imsr.imsrPutApi("/devices", values);
        isLoad.value = false;
        router.push({
          name: "devicesView",
          params: {
            projectId: route.params.projectId,
            deviceId: route.params.deviceId,
          },
        });
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };

    const openAiLevelHelpModal = () => {
      aiLevelHelpModal.value = true;
    };

    const closeAiLevelHelpModal = () => {
      aiLevelHelpModal.value = false;
    };

    return {
      schema,
      onSubmit,
      isLoad,
      formValues,
      breadcrumb,
      deviceType,
      aiLevelHelpModal,
      openAiLevelHelpModal,
      closeAiLevelHelpModal,
      lang: computed(() => {
        return locale.value;
      }),
    };
  },
};
</script>
